import Vue from "vue";
import Component from "vue-class-component";
import { namespace, Mutation } from "vuex-class";
import Chart from "chart.js";
import moment from "moment";
import "./antd-styles";
import {
	Card,
	Statistic,
	Table,
	Select,
	Dropdown,
	Menu,
	Checkbox,
	Button,
	Tooltip,
	Icon,
	Empty,
	DatePicker,
	Spin
} from "ant-design-vue";

import marketingServices from "../../../services/marketing.services";
import { storeModules } from "../../../store";
import { DrawerModel, IDrawerModel } from "../../../models/common/drawer.model";
import { DRAWER_NAME } from "../../../common/drawer-component.constants";
import {
	IMarketingDashboardData,
	IMarketingFilter,
	IMarketingFilterOptions,
	IMarketingCampaignRow,
	IMarketingTrackingRecord
} from "../../../models/marketing/marketing-tracking.model";

const CommonMutation = namespace(storeModules.Common, Mutation);

const SourceIcon = () => import("./components/source-icon");

// Source filter fallback when the BE filter-options haven't loaded (matches the Figma list).
const DEFAULT_SOURCES = ["google", "facebook", "instagram", "vimeo", "gmail", "other"];

const COLOR_ORGANIC = "#2f54eb"; // geekblue â€“ matches Figma "Organic"
const COLOR_ADS = "#f5222d"; // red â€“ matches Figma "From ads"

const DATE_RANGE_OPTIONS = [
	{ value: "today", labelKey: "MARKETING_DATE_TODAY" },
	{ value: "yesterday", labelKey: "MARKETING_DATE_YESTERDAY" },
	{ value: "last24h", labelKey: "MARKETING_DATE_LAST24H" },
	{ value: "lastweek", labelKey: "MARKETING_DATE_LASTWEEK" },
	{ value: "lastmonth", labelKey: "MARKETING_DATE_LASTMONTH" },
	{ value: "thisweek", labelKey: "MARKETING_DATE_THISWEEK" },
	{ value: "custom", labelKey: "MARKETING_DATE_CUSTOM" }
];

@Component({
	name: "MarketingDashboard",
	template: require("./template.html"),
	components: {
		"a-card": Card,
		"a-statistic": Statistic,
		"a-table": Table,
		"a-select": Select,
		"a-select-option": Select.Option,
		"a-dropdown": Dropdown,
		"a-menu": Menu,
		"a-checkbox": Checkbox,
		"a-checkbox-group": Checkbox.Group,
		"a-button": Button,
		"a-tooltip": Tooltip,
		"a-icon": Icon,
		"a-empty": Empty,
		"a-range-picker": DatePicker.RangePicker,
		"a-spin": Spin,
		"source-icon": SourceIcon
	}
})
export default class MarketingDashboard extends Vue {
	@CommonMutation setVisibleDrawerModel: (payload: IDrawerModel) => void;

	loading = false;
	data: IMarketingDashboardData = null;
	chart: any = null;
	loadSeq = 0; // bumped on each successful load to force antd cards/tables to re-render
	reqSeq = 0; // monotonic load id; results from a superseded (older) request are discarded
	recordsInFlight = 0; // outstanding tracking-record requests; spinner clears at 0
	campaignsInFlight = 0; // outstanding campaign requests; spinner clears at 0

	// Filter state
	dateRange = "thisweek";
	customRange: any[] = []; // [from, to] moments when dateRange === 'custom'
	selectedSources: string[] = [];
	selectedCampaigns: string[] = [];
	selectedTerms: string[] = [];

	dateRangeOptions = DATE_RANGE_OPTIONS;

	filterOptions: IMarketingFilterOptions = null;

	// Infinite-scroll state (server-side via the paged endpoints).
	pageSize = 20;
	campaignsRows: IMarketingCampaignRow[] = [];
	recordsRows: IMarketingTrackingRecord[] = [];
	campaignsPage = 1;
	recordsPage = 1;
	campaignsTotal = 0;
	recordsTotal = 0;
	campaignsLoading = false;
	recordsLoading = false;

	// Sources offered by the source filter â€” from the BE when available, else the default list.
	get allSources(): string[] {
		return this.filterOptions && this.filterOptions.Sources && this.filterOptions.Sources.length
			? this.filterOptions.Sources
			: DEFAULT_SOURCES;
	}

	get isCustomRange(): boolean {
		return this.dateRange === "custom";
	}

	// Only render the dashboard once a complete payload is present â€” guards the
	// template against a malformed/partial response crashing the render.
	get hasData(): boolean {
		return !!(this.data && this.data.Summary);
	}

	get filter(): IMarketingFilter {
		const range = this.resolveDateRange();
		return {
			FromDate: range.FromDate,
			ToDate: range.ToDate,
			Sources: this.selectedSources,
			Campaigns: this.selectedCampaigns,
			Terms: this.selectedTerms
		};
	}

	// Resolves the active date filter to a concrete From/To range (ISO date).
	private resolveDateRange(): { FromDate: string | null; ToDate: string | null } {
		const fmt = "YYYY-MM-DD";
		if (this.isCustomRange) {
			if (this.customRange && this.customRange.length === 2) {
				return {
					FromDate: moment(this.customRange[0]).format(fmt),
					ToDate: moment(this.customRange[1]).format(fmt)
				};
			}
			return { FromDate: null, ToDate: null };
		}
		const now = moment();
		switch (this.dateRange) {
			case "today":
				return { FromDate: now.clone().startOf("day").format(fmt), ToDate: now.format(fmt) };
			case "yesterday": {
				const y = now.clone().subtract(1, "day");
				return { FromDate: y.clone().startOf("day").format(fmt), ToDate: y.clone().endOf("day").format(fmt) };
			}
			case "last24h":
				return { FromDate: now.clone().subtract(24, "hours").format(fmt), ToDate: now.format(fmt) };
			case "lastweek":
				return {
					FromDate: now.clone().subtract(1, "week").startOf("isoWeek").format(fmt),
					ToDate: now.clone().subtract(1, "week").endOf("isoWeek").format(fmt)
				};
			case "lastmonth":
				return {
					FromDate: now.clone().subtract(1, "month").startOf("month").format(fmt),
					ToDate: now.clone().subtract(1, "month").endOf("month").format(fmt)
				};
			case "thisweek":
				return { FromDate: now.clone().startOf("isoWeek").format(fmt), ToDate: now.clone().endOf("isoWeek").format(fmt) };
			default:
				return { FromDate: null, ToDate: null };
		}
	}

	onDateRangeChange() {
		// Re-query immediately for presets; for "custom" wait until a range is picked.
		if (!this.isCustomRange) this.applyFilter();
	}

	// Compact label for a multi-select filter: the placeholder when empty, the
	// single value when one is chosen, otherwise "N selected" (avoids overflow).
	multiLabel(selected: string[], zeroKey: string): string {
		const self = this as any;
		if (!selected || !selected.length) return self.$t(zeroKey);
		if (selected.length === 1) return selected[0];
		return self.$t("MARKETING_N_SELECTED", { count: selected.length });
	}

	get campaignOptions(): string[] {
		if (this.filterOptions && this.filterOptions.Campaigns && this.filterOptions.Campaigns.length) return this.filterOptions.Campaigns;
		if (!this.data) return [];
		return Array.from(new Set(this.data.Campaigns.map(c => c.Campaign)));
	}

	get termOptions(): string[] {
		if (this.filterOptions && this.filterOptions.Terms && this.filterOptions.Terms.length) return this.filterOptions.Terms;
		if (!this.data) return [];
		const terms = this.data.Campaigns.map(c => c.UTMTerm)
			.concat(this.data.TrackingRecords.map(r => r.UTMTerm));
		return Array.from(new Set(terms));
	}

	get maxSourceLeads(): number {
		if (!this.data || !this.data.TopSources.length) return 0;
		return Math.max(...this.data.TopSources.map(s => s.Leads));
	}

	// Top Sources chip width is proportional to its lead count (a mini bar chart).
	sourceBarWidth(leads: number): string {
		const max = this.maxSourceLeads;
		if (!max) return "0%";
		return `${(leads / max) * 100}%`;
	}

	// Ant Design table column definitions ------------------------------------
	get campaignColumns() {
		const self = this as any;
		return [
			{ title: self.$t("MARKETING_COL_CAMPAIGN"), dataIndex: "Campaign", scopedSlots: { customRender: "campaign" } },
			{ title: self.$t("MARKETING_COL_SOURCE"), dataIndex: "Source", scopedSlots: { customRender: "source" } },
			{ title: self.$t("MARKETING_COL_UTM_TERM"), dataIndex: "UTMTerm" },
			{ title: self.$t("MARKETING_COL_LEADS"), dataIndex: "Leads", align: "right" },
			{ title: self.$t("MARKETING_COL_PERCENT"), dataIndex: "Percent", align: "right", scopedSlots: { customRender: "percent" } }
		];
	}

	get recordColumns() {
		const self = this as any;
		return [
			{ title: self.$t("MARKETING_COL_PERSON"), dataIndex: "Person", scopedSlots: { customRender: "person" } },
			{ title: self.$t("MARKETING_COL_SOURCE"), dataIndex: "Source", scopedSlots: { customRender: "source" } },
			{ title: self.$t("MARKETING_COL_TOUCH"), dataIndex: "Touch", scopedSlots: { customRender: "touch" } },
			{ title: self.$t("MARKETING_COL_DATE"), dataIndex: "Date", align: "right" }
		];
	}

	async mounted() {
		this.loadFilterOptions();
		await this.loadData();
		// Wait for the chart card (v-if="hasData") to be in the DOM before
		// grabbing its canvas ref, otherwise the first render is skipped.
		await this.$nextTick();
		this.renderChart();
	}

	async loadFilterOptions() {
		try {
			this.filterOptions = await marketingServices.getFilterOptions();
		} catch (e) {
			this.filterOptions = null; // fall back to dashboard-derived options
		}
	}

	beforeDestroy() {
		this.destroyChart();
	}

	private destroyChart() {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	}

	async loadData() {
		const seq = ++this.reqSeq;
		this.loading = true;
		let ok = false;
		try {
			const data = await marketingServices.getDashboard(this.filter);
			if (seq !== this.reqSeq) return; // a newer filter change superseded this load
			this.data = data;
			this.loadSeq++;
			ok = true;
		} catch (e) {
			if (seq === this.reqSeq) {
				// Drop stale data so KPIs/chart don't show the old filter, and discard
				// the chart so the next load rebuilds it on a freshly mounted canvas.
				this.data = null;
				this.destroyChart();
			}
			console.error("Marketing dashboard load failed", e);
		} finally {
			if (seq === this.reqSeq) this.loading = false;
		}
		// The tables share the dashboard's visibility (hasData); only load them when
		// the summary succeeded so a failed dashboard doesn't fire hidden requests.
		if (seq !== this.reqSeq || !ok) return;
		this.loadRecordsPage(1, false, seq);
		this.loadCampaignsPage(1, false, seq);
	}

	get recordsHasMore(): boolean {
		return this.recordsRows.length < this.recordsTotal;
	}

	get campaignsHasMore(): boolean {
		return this.campaignsRows.length < this.campaignsTotal;
	}

	async loadRecordsPage(page: number, append: boolean, seq = this.reqSeq) {
		// Only block stacking load-more requests; a fresh filter load must never be
		// skipped just because an earlier request is still in flight.
		if (append && this.recordsLoading) return;
		this.recordsInFlight++;
		this.recordsLoading = true;
		try {
			const paged = await marketingServices.getTrackingRecords(this.filter, page, this.pageSize);
			if (seq !== this.reqSeq) return; // a newer filter change superseded this request
			const items = paged.Items || [];
			this.recordsRows = append ? this.recordsRows.concat(items) : items;
			this.recordsPage = page;
			// A short/empty page means there are no more pages â€” clamp Total to what we
			// actually have so hasMore can't stay true on a stale/overstated count
			// (otherwise the scroll auto-fill would fetch empty pages forever).
			this.recordsTotal = items.length < this.pageSize ? this.recordsRows.length : paged.Total;
		} catch (e) {
			// On a fresh load (filter change) drop stale rows; keep already-loaded
			// pages if only a load-more failed.
			if (!append && seq === this.reqSeq) {
				this.recordsRows = [];
				this.recordsTotal = 0;
			}
			console.error("Load tracking-records page failed", e);
		} finally {
			// Track outstanding requests instead of the seq: every request decrements,
			// so the spinner clears once the last one settles (a superseded request
			// whose successor never runs â€” e.g. dashboard reload failed â€” won't hang it).
			this.recordsInFlight--;
			this.recordsLoading = this.recordsInFlight > 0;
		}
	}

	async loadCampaignsPage(page: number, append: boolean, seq = this.reqSeq) {
		if (append && this.campaignsLoading) return;
		this.campaignsInFlight++;
		this.campaignsLoading = true;
		try {
			const paged = await marketingServices.getCampaigns(this.filter, page, this.pageSize);
			if (seq !== this.reqSeq) return; // a newer filter change superseded this request
			const items = paged.Items || [];
			this.campaignsRows = append ? this.campaignsRows.concat(items) : items;
			this.campaignsPage = page;
			// A short/empty page means there are no more pages â€” clamp Total so the
			// scroll auto-fill can't fetch empty pages forever on a stale count.
			this.campaignsTotal = items.length < this.pageSize ? this.campaignsRows.length : paged.Total;
		} catch (e) {
			if (!append && seq === this.reqSeq) {
				this.campaignsRows = [];
				this.campaignsTotal = 0;
			}
			console.error("Load campaigns page failed", e);
		} finally {
			// Track outstanding requests instead of the seq: the spinner clears once the
			// last one settles, so a superseded request whose successor never runs
			// (e.g. dashboard reload failed) can't leave it stuck on.
			this.campaignsInFlight--;
			this.campaignsLoading = this.campaignsInFlight > 0;
		}
	}

	// Called by v-infinite-scroll when the table is scrolled near the bottom.
	loadMoreRecords() {
		if (this.recordsLoading || !this.recordsHasMore) return;
		this.loadRecordsPage(this.recordsPage + 1, true);
	}

	loadMoreCampaigns() {
		if (this.campaignsLoading || !this.campaignsHasMore) return;
		this.loadCampaignsPage(this.campaignsPage + 1, true);
	}

	async applyFilter() {
		await this.loadData();
		// hasData may have just flipped falseâ†’true, so let the canvas mount first.
		await this.$nextTick();
		this.updateChart();
	}

	openPerson(personId: number) {
		if (!personId) return;
		this.setVisibleDrawerModel(new DrawerModel({
			componentName: DRAWER_NAME.PERSON_DETAIL,
			props: { objectId: personId },
			title: "PERSONS"
		}));
	}

	// Row keys include the index since records/campaigns can legitimately repeat
	// the same content (e.g. same person, touch and date across campaigns).
	campaignRowKey(row: any, index: number): string {
		return `${row.Campaign}|${row.Source}|${row.UTMTerm}|${index}`;
	}

	recordRowKey(row: any, index: number): string {
		return `${row.PersonId}|${row.Touch}|${row.Date}|${index}`;
	}

	// Chart -------------------------------------------------------------------
	private buildChartConfig() {
		const daily = (this.data && this.data.Daily) || [];
		const self = this as any;
		return {
			type: "bar",
			data: {
				labels: daily.map(d => d.Label),
				datasets: [
					{
						label: self.$t("MARKETING_ORGANIC"),
						backgroundColor: COLOR_ORGANIC,
						data: daily.map(d => d.Organic),
						barPercentage: 0.6,
						categoryPercentage: 0.6
					},
					{
						label: self.$t("MARKETING_FROM_ADS"),
						backgroundColor: COLOR_ADS,
						data: daily.map(d => d.FromAds),
						barPercentage: 0.6,
						categoryPercentage: 0.6
					}
				]
			},
			options: {
				maintainAspectRatio: false,
				legend: { position: "bottom", align: "start", labels: { boxWidth: 14, padding: 16 } },
				tooltips: { mode: "index", intersect: false },
				scales: {
					xAxes: [{ stacked: true, gridLines: { display: false } }],
					yAxes: [{ stacked: true, ticks: { beginAtZero: true } }]
				}
			}
		};
	}

	private renderChart() {
		const ctx = this.$refs.dailyChart as HTMLCanvasElement;
		if (!ctx) return;
		this.chart = new Chart(ctx, this.buildChartConfig() as any);
	}

	private updateChart() {
		if (!this.chart) {
			this.renderChart();
			return;
		}
		const daily = (this.data && this.data.Daily) || [];
		this.chart.data.labels = daily.map(d => d.Label);
		this.chart.data.datasets[0].data = daily.map(d => d.Organic);
		this.chart.data.datasets[1].data = daily.map(d => d.FromAds);
		this.chart.update();
	}

	handleExport() {
		// Real export will be wired to a BE endpoint (BAC-2786). For now export
		// the rows actually loaded into the table (filter-consistent) as CSV.
		if (!this.recordsRows.length) return;
		const rows = [["Person", "Source", "UTM_term", "Touch", "Date"]].concat(
			this.recordsRows.map(r => [r.Person, r.Source, r.UTMTerm, r.Touch, r.Date])
		);
		// RFC 4180: wrap every field in quotes and double any embedded quote.
		const csv = rows.map(r => r.map(c => `"${String(c == null ? "" : c).replace(/"/g, '""')}"`).join(",")).join("\n");
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const a = document.createElement("a");
		a.href = URL.createObjectURL(blob);
		a.download = "marketing-tracking.csv";
		a.click();
		URL.revokeObjectURL(a.href);
	}
}
